'use client';

import { useState } from 'react';
import * as formStyles from './styles/formStyles';

interface PropertyTransactionFormProps {
  dictionary: any;
  disableSubmit?: boolean;
  onSubmit: (propertyInfo: {
    epcValue: string;
    condition: string;
  }) => void;
  transactionCount?: number;
}

export default function PropertyTransactionForm({ dictionary, onSubmit, disableSubmit = false, transactionCount }: PropertyTransactionFormProps) {
  const [errors, setErrors] = useState<{
    epcValue?: string;
    condition?: string;
  }>({});
  // State to track dropdown selections
  const [epcValue, setEpcValue] = useState('');
  const [conditionValue, setConditionValue] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const propertyInfo = {
      epcValue: formData.get('epcValue') as string,
      condition: formData.get('condition') as string,
    };

    const newErrors: typeof errors = {};

    if (!propertyInfo.epcValue) {
      newErrors.epcValue = dictionary?.sellingForm?.requiredField;
    }
    if (!propertyInfo.condition) {
      newErrors.condition = dictionary?.sellingForm?.requiredField;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit(propertyInfo);
  };

  // Define common input classes using imported constants
  const inputClasses = formStyles.inputElementBaseClasses;
  const selectSpecificClasses = formStyles.selectElementSpecificClasses;

  // Prepare the transaction message with the count injected
  let transactionMessageHtml = dictionary?.sellingForm?.transactionMessage || '';
  if (transactionCount !== undefined && transactionCount !== null && transactionMessageHtml) {
    transactionMessageHtml = transactionMessageHtml.replace('[TRANSACTION_COUNT]', String(transactionCount));
  } else {
    // Fallback or hide message if count is not available or key is missing?
    if (transactionMessageHtml.includes('[TRANSACTION_COUNT]')) {
      transactionMessageHtml = ''; // Clear message if count missing but expected
    }
  }

  return (
    // Use full width and remove mb-4 to let parent control bottom margin
    <form onSubmit={handleSubmit} className="w-full">
      {/* Adjusted text styling and spacing */}
      <div className="space-y-1">
        {/* Display the processed transaction message */}
        {transactionMessageHtml && (
          <p className="text-base text-zinc-800" dangerouslySetInnerHTML={{ __html: transactionMessageHtml }} />
        )}
        <p className="text-base text-zinc-800">
          {dictionary?.sellingForm?.transactionMessage2}
        </p>
      </div>

      {/* --- EPC Dropdown (Row 1) --- */}
      {/* Apply base container classes, ensure mt-2 for spacing */}
      <div className="w-[75%] mt-2">
        <div className={`${formStyles.inputContainerBaseClasses}`}> {/* Select container */}
          <select
            name="epcValue"
            required
            // Apply new select base class
            className={formStyles.selectElementBaseClasses}
            value={epcValue}
            onChange={(e) => setEpcValue(e.target.value)}
          >
            {/* Use constant for placeholder styling */}
            <option value="" disabled className="text-zinc-500">{dictionary?.sellingForm?.propertyEpc || 'EPC value'}</option>
            {/* Use new dictionary keys for display text */}
            <option value="A" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcLabelA || 'EPC value A'}</option>
            <option value="B" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcLabelB || 'EPC value B'}</option>
            <option value="C" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcLabelC || 'EPC value C'}</option>
            <option value="D" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcLabelD || 'EPC value D'}</option>
            <option value="E" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcLabelE || 'EPC value E'}</option>
            <option value="F" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcLabelF || 'EPC value F'}</option>
            <option value="G" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcLabelG || 'EPC value G'}</option>
            <option value="unknown" className={formStyles.selectElementSpecificClasses}>{dictionary?.sellingForm?.propertyEpcUnknown || 'Unknown'}</option>
          </select>
        </div>
        {/* Use constant for error message classes */} 
        {errors.epcValue && <p className={formStyles.errorMessageClasses}>{errors.epcValue}</p>}
      </div>

      {/* --- Condition Dropdown (Row 2) --- */}
      {/* Apply base container classes, ensure mt-2 for spacing */} 
      <div className="w-[75%] mt-2">
        <div className={`${formStyles.inputContainerBaseClasses}`}> {/* Select container */}
          <select
            name="condition"
            required
            // Apply new select base class
            className={formStyles.selectElementBaseClasses}
            value={conditionValue}
            onChange={(e) => setConditionValue(e.target.value)}
          >
            {/* Use constant for placeholder styling */}
            <option value="" disabled className="text-zinc-500">{dictionary?.propertyForm?.condition || 'Home Condition'}</option>
            {/* Use updated dictionary keys for display text */}
            <option value="1" className={formStyles.selectElementSpecificClasses}>{dictionary?.propertyForm?.condition1 || 'State: 5/5 As new'}</option>
            <option value="2" className={formStyles.selectElementSpecificClasses}>{dictionary?.propertyForm?.condition2 || 'State: 4/5 Very good'}</option>
            <option value="3" className={formStyles.selectElementSpecificClasses}>{dictionary?.propertyForm?.condition3 || 'State: 3/5 Signs of use'}</option>
            <option value="4" className={formStyles.selectElementSpecificClasses}>{dictionary?.propertyForm?.condition4 || 'State: 2/5 Minor defects'}</option>
            <option value="5" className={formStyles.selectElementSpecificClasses}>{dictionary?.propertyForm?.condition5 || 'State: 1/5 Major defects'}</option>
            <option value="6" className={formStyles.selectElementSpecificClasses}>{dictionary?.propertyForm?.condition6 || 'State: 0/5 Uninhabitable'}</option>
          </select>
        </div>
        {/* Use constant for error message classes */} 
        {errors.condition && <p className={formStyles.errorMessageClasses}>{errors.condition}</p>}
      </div>
      {/* --- End EPC and Condition --- */}

      {/* Conditionally render button only when both dropdowns are selected */}
      {epcValue && conditionValue && (
        <button
          type="submit"
          disabled={disableSubmit}
          // Apply button classes using constants
          className={`${formStyles.submitButtonBaseClasses} ${
            disableSubmit ? formStyles.submitButtonDisabledClasses : formStyles.submitButtonHoverClasses
          }`}
        >
          {/* Using generic submit key */}
          {dictionary?.sellingForm?.submit || 'Submit'}
        </button>
      )}
    </form>
  );
} 